/**
 * Algorithmic Explore Connections Service (v1)
 *
 * Purely algorithmic matching based on tag overlap.
 * Always returns exactly 3 songs (or fewer if DB has <3 other published songs).
 *
 * No pinned/curated selections in v1.
 */

import { supabase } from './supabaseClient';

export interface SongForConnections {
  id: string;
  title: string;
  artist: string;
  image_url?: string | null;
  tags?: string[];
  primary_tags?: string[];
  secondary_tags?: string[];
  explore_caption?: string;
  caption?: string;
  published_at?: string;
  created_at?: string;
  updated_at?: string;
}

interface ScoredSong {
  song: SongForConnections;
  score: number;
}

/**
 * Get tie-breaker date for stable sorting
 */
function getTieBreakerDate(song: SongForConnections): string {
  return song.published_at || song.updated_at || song.created_at || '';
}

/**
 * Stable comparator for scored songs
 * Order: score DESC → date DESC → id ASC
 */
function stableCompare(a: ScoredSong, b: ScoredSong): number {
  if (b.score !== a.score) {
    return b.score - a.score;
  }

  const dateA = getTieBreakerDate(a.song);
  const dateB = getTieBreakerDate(b.song);
  if (dateA !== dateB) {
    return dateB.localeCompare(dateA);
  }

  return a.song.id.localeCompare(b.song.id);
}

/**
 * Calculate relevance score
 * +3 per shared primary tag
 * +1 per shared secondary tag
 */
function calculateScore(current: SongForConnections, candidate: SongForConnections): number {
  let score = 0;

  const currentPrimaryTags = new Set(
    (current.primary_tags || current.tags || []).map(t => t.toLowerCase())
  );
  const currentSecondaryTags = new Set(
    (current.secondary_tags || []).map(t => t.toLowerCase())
  );

  const candidatePrimaryTags = (candidate.primary_tags || candidate.tags || []).map(t =>
    t.toLowerCase()
  );
  const candidateSecondaryTags = (candidate.secondary_tags || []).map(t => t.toLowerCase());

  candidatePrimaryTags.forEach(tag => {
    if (currentPrimaryTags.has(tag)) {
      score += 3;
    }
  });

  candidateSecondaryTags.forEach(tag => {
    if (currentSecondaryTags.has(tag)) {
      score += 1;
    }
  });

  return score;
}

/**
 * Fetch a song with its tags using JOINs
 */
async function fetchSongWithTags(songId: string): Promise<SongForConnections | null> {
  const { data, error } = await supabase.rpc('get_song_with_tags', { p_song_id: songId });

  if (error) {
    console.error('[fetchSongWithTags] Error:', error);
    return null;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return data[0] as SongForConnections;
}

/**
 * Fetch published candidate songs (excluding current song) with their tags
 */
async function fetchCandidateSongsWithTags(
  excludeId: string
): Promise<SongForConnections[]> {
  const { data, error } = await supabase.rpc('get_all_songs_with_tags', {
    p_exclude_song_id: excludeId,
  });

  if (error) {
    console.error('[fetchCandidateSongsWithTags] Error:', error);
    return [];
  }

  return (data || []) as SongForConnections[];
}

/**
 * Shuffle an array using Fisher-Yates algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Get related songs using algorithmic matching
 * Always returns exactly 3 (or fewer if DB has <3 other published songs)
 * ONLY includes songs with at least one shared tag (score > 0)
 *
 * @param currentSong - The current song
 * @returns Array of up to 3 related songs
 */
export async function getRelatedSongs(
  currentSong: SongForConnections
): Promise<SongForConnections[]> {
  const songWithTags = await fetchSongWithTags(currentSong.id);

  if (!songWithTags) {
    return [];
  }

  const candidates = await fetchCandidateSongsWithTags(currentSong.id);

  if (candidates.length === 0) {
    return [];
  }

  const scoredCandidates: ScoredSong[] = candidates.map(candidate => ({
    song: candidate,
    score: calculateScore(songWithTags, candidate),
  }));

  const relatedOnly = scoredCandidates.filter(sc => sc.score > 0);

  if (relatedOnly.length === 0) {
    return [];
  }

  relatedOnly.sort(stableCompare);

  return relatedOnly.slice(0, 3).map(sc => sc.song);
}

/**
 * Get shuffled related songs from a larger pool of top candidates
 * Picks 3 random songs from the top 15 scored candidates
 * Provides variety while maintaining relevance
 * ONLY includes songs with at least one shared tag (score > 0)
 * Avoids recently shown songs when possible
 *
 * @param currentSong - The current song
 * @param excludeSongIds - Array of song IDs to avoid showing (previously shown songs)
 * @returns Array of up to 3 shuffled related songs
 */
export async function getShuffledRelatedSongs(
  currentSong: SongForConnections,
  excludeSongIds: string[] = []
): Promise<SongForConnections[]> {
  const songWithTags = await fetchSongWithTags(currentSong.id);

  if (!songWithTags) {
    return [];
  }

  const candidates = await fetchCandidateSongsWithTags(currentSong.id);

  if (candidates.length === 0) {
    return [];
  }

  const scoredCandidates: ScoredSong[] = candidates.map(candidate => ({
    song: candidate,
    score: calculateScore(songWithTags, candidate),
  }));

  const relatedOnly = scoredCandidates.filter(sc => sc.score > 0);

  if (relatedOnly.length === 0) {
    return [];
  }

  relatedOnly.sort(stableCompare);

  const topCandidates = relatedOnly.slice(0, Math.min(15, relatedOnly.length));

  const unseenCandidates = topCandidates.filter(
    sc => !excludeSongIds.includes(sc.song.id)
  );

  const candidatesToUse = unseenCandidates.length >= 3 ? unseenCandidates : topCandidates;

  if (candidatesToUse.length <= 3) {
    return candidatesToUse.map(sc => sc.song);
  }

  const shuffled = shuffleArray(candidatesToUse);
  return shuffled.slice(0, 3).map(sc => sc.song);
}

/**
 * Alias for backward compatibility
 */
export const getStableExploreConnections = getRelatedSongs;

export type { ScoredSong };
